import React, { useEffect, useState, useCallback } from 'react';
import { observabilityService } from '../../services/api';
import { Activity, AlertTriangle, CheckCircle, XCircle, Clock, RefreshCw, Server, Wifi, WifiOff } from 'lucide-react';
import DeploymentCard from '../../components/observability/DeploymentCard';
import IncidentList   from '../../components/observability/IncidentList';
import DeploymentMetricsModal from '../../components/observability/DeploymentMetricsModal';
import './Observability.css';

const STATUS_ICON = {
  online:   <CheckCircle size={14} color="var(--success)" />,
  degraded: <AlertTriangle size={14} color="var(--warning)" />,
  critical: <XCircle size={14} color="var(--danger)" />,
  offline:  <WifiOff size={14} color="var(--danger)" />,
  unknown:  <Clock size={14} color="var(--text-muted)" />,
};

export default function Observability() {
  const [overview, setOverview]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [lastRefresh, setLastRefresh]   = useState(null);
  const [selectedDep, setSelectedDep]   = useState(null);
  const [view, setView]                 = useState('grid'); // grid | incidents

  const load = useCallback(async () => {
    try {
      const res = await observabilityService.getOverview();
      setOverview(res.data);
      setLastRefresh(new Date());
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  const sc = overview?.statusCounts || {};
  const totalOnline   = sc.online   || 0;
  const totalDegraded = sc.degraded || 0;
  const totalCritical = (sc.critical || 0) + (sc.offline || 0);
  const totalUnknown  = sc.unknown  || 0;

  // Group deployments by client
  const byClient = {};
  (overview?.deployments || []).forEach(d => {
    const key = d.client?._id || 'unknown';
    if (!byClient[key]) byClient[key] = { client: d.client, deployments: [] };
    byClient[key].deployments.push(d);
  });

  return (
    <div className="obs-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Observability</h1>
          <p className="page-subtitle">
            Control tower — {overview?.deployments?.length || 0} deployments across {overview?.totalClients || 0} clients
            {lastRefresh && <span className="refresh-ts"> · Updated {lastRefresh.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className={`btn btn-ghost ${view==='grid' ? 'btn-active' : ''}`} onClick={() => setView('grid')}>
            <Server size={14} /> Deployments
          </button>
          <button className={`btn btn-ghost ${view==='incidents' ? 'btn-active' : ''}`} onClick={() => setView('incidents')}>
            <AlertTriangle size={14} /> Incidents
            {(overview?.openIncidents?.length || 0) > 0 && (
              <span className="badge-count">{overview.openIncidents.length}</span>
            )}
          </button>
          <button className="btn btn-ghost" onClick={load} title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Status bar */}
      <div className="obs-status-bar">
        <div className="obs-status-pill obs-status-pill--online">
          <CheckCircle size={14} /> <span>{totalOnline} Online</span>
        </div>
        <div className="obs-status-pill obs-status-pill--degraded">
          <AlertTriangle size={14} /> <span>{totalDegraded} Degraded</span>
        </div>
        <div className="obs-status-pill obs-status-pill--critical">
          <XCircle size={14} /> <span>{totalCritical} Critical/Offline</span>
        </div>
        <div className="obs-status-pill obs-status-pill--unknown">
          <Clock size={14} /> <span>{totalUnknown} Unknown</span>
        </div>
        <div className="obs-status-pill obs-status-pill--incident">
          <Activity size={14} /> <span>{overview?.openIncidents?.length || 0} Open Incidents</span>
        </div>
      </div>

      {loading && <div className="loading">Loading deployments...</div>}

      {!loading && view === 'grid' && (
        <div className="obs-client-groups">
          {Object.values(byClient).map(({ client, deployments }) => (
            <div key={client?._id} className="obs-client-group">
              <div className="obs-client-header">
                <Wifi size={14} />
                <span className="obs-client-name">{client?.name}</span>
                <span className="obs-client-code">{client?.code}</span>
                <span className="obs-client-type">{client?.deploymentType}</span>
              </div>
              <div className="obs-dep-grid">
                {deployments.map(dep => (
                  <DeploymentCard
                    key={dep._id}
                    deployment={dep}
                    onClick={() => setSelectedDep(dep)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && view === 'incidents' && (
        <IncidentList
          incidents={overview?.openIncidents || []}
          onRefresh={load}
        />
      )}

      {selectedDep && (
        <DeploymentMetricsModal
          deployment={selectedDep}
          onClose={() => setSelectedDep(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
}
